import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, firstValueFrom, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { NIKO_API_URL } from '../tokens/api.tokens';
import { PaginatedResult } from '../models/paginated-result.model';
import {
  IMPERSONATION_STORAGE_KEY,
  ImpersonationCandidate,
  ImpersonationMeta,
  ImpersonationSession,
  ImpersonationSessionsQuery,
  StartImpersonationBody,
  StartImpersonationResponse,
} from '../models/impersonation.model';
import { UserService } from './user.service';

/** One-shot i18n key shown after the hard reload that follows an unexpected end (expired / revoked). */
const PENDING_NOTICE_KEY = 'niko_impersonation_notice';

/**
 * Client side of the impersonation session (BR-AUTH-02/03).
 *
 * Owns the two `localStorage` keys, the HTTP calls under
 * `/auth/impersonate` and the ONLY way to leave a session (`exit`). Entering
 * or leaving a session is an identity change, so both end in a full page
 * reload: every NgRx slice (prospects, catalogs, users, permissions) was
 * loaded as the previous identity and the store has no "reset everything"
 * action — a client-side navigation would keep showing the actor's data to
 * the impersonated user and vice versa.
 *
 * `meta` is exposed as a signal so `libs/ui` code (the banner's localStorage
 * mirror, the token interceptor) can react without importing the store; the
 * store keeps its own copy from
 * `GET /users/me` for the app layer.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private readonly http = inject(HttpClient);
  private readonly base = inject(NIKO_API_URL);
  private readonly document = inject(DOCUMENT);
  private readonly userService = inject(UserService);

  private readonly _meta = signal<ImpersonationMeta | null>(this.readMeta());

  /** Current session metadata, `null` when not impersonating. */
  readonly meta = this._meta.asReadonly();
  readonly isActive = computed(() => this._meta() !== null);

  // ── HTTP ─────────────────────────────────────────────────────────────────

  /**
   * `POST /auth/impersonate`, then `GET /users/me` with the new token so the
   * stored metadata is exactly what the backend reports (the start response
   * carries no `sessionId`/`actorName`). If the second call fails the token
   * is dropped again: a token without metadata would render no banner.
   */
  start(body: StartImpersonationBody): Observable<ImpersonationMeta> {
    return this.http
      .post<StartImpersonationResponse>(`${this.base}/auth/impersonate`, body)
      .pipe(
        switchMap((res) => {
          localStorage.setItem(IMPERSONATION_STORAGE_KEY.SESSION, res.accessToken);
          return this.userService.getMe().pipe(
            map(({ impersonation }) => {
              if (!impersonation) throw new Error('IMPERSONATION_META_MISSING');
              this.setSession(res.accessToken, impersonation);
              return impersonation;
            }),
            catchError((err: unknown) => {
              this.clear();
              return throwError(() => err);
            }),
          );
        }),
      );
  }

  /** `POST /auth/impersonate/stop` — 204, idempotent, outside the black list. */
  stop(): Observable<void> {
    return this.http.post<void>(`${this.base}/auth/impersonate/stop`, {});
  }

  /** Users of the CURRENT tenant the caller may impersonate (minus itself and privileged users). */
  listCandidates(): Observable<ImpersonationCandidate[]> {
    return this.http.get<ImpersonationCandidate[]>(`${this.base}/auth/impersonate/candidates`);
  }

  // ── admin (SuperAdmin only) ──────────────────────────────────────────────

  /**
   * `GET /auth/impersonate/sessions`. `active` is sent only when true: the
   * backend treats any other value as "no filter", so sending `false` would
   * be noise in the URL.
   */
  listSessions(query: ImpersonationSessionsQuery): Observable<PaginatedResult<ImpersonationSession>> {
    let params = new HttpParams().set('page', query.page).set('limit', query.limit);
    if (query.tenantId) params = params.set('tenantId', query.tenantId);
    if (query.active) params = params.set('active', 'true');
    return this.http.get<PaginatedResult<ImpersonationSession>>(
      `${this.base}/auth/impersonate/sessions`,
      { params },
    );
  }

  /** `PATCH /auth/impersonate/sessions/:id/revoke` — 204; the target's next request fails with IMPERSONATION_REVOKED. */
  revokeSession(id: string): Observable<void> {
    return this.http.patch<void>(`${this.base}/auth/impersonate/sessions/${id}/revoke`, {});
  }

  // ── storage ──────────────────────────────────────────────────────────────

  getToken(): string | null {
    return localStorage.getItem(IMPERSONATION_STORAGE_KEY.SESSION);
  }

  getMeta(): ImpersonationMeta | null {
    return this._meta();
  }

  setSession(token: string, meta: ImpersonationMeta): void {
    localStorage.setItem(IMPERSONATION_STORAGE_KEY.SESSION, token);
    this.writeMeta(meta);
  }

  /** Refreshes the cached metadata from `GET /users/me` (F5 rehydration). Never touches the token. */
  syncMeta(meta: ImpersonationMeta | null): void {
    if (!this.getToken()) return;
    if (meta) this.writeMeta(meta);
  }

  /** Removes ONLY the impersonation keys — the actor's `niko_tenant_session` must survive (plan §7). */
  clear(): void {
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY.SESSION);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY.META);
    this._meta.set(null);
  }

  // ── leaving ──────────────────────────────────────────────────────────────

  /**
   * Ends the session on the client and reloads the app as the actor.
   *
   * `notifyBackend` is false when the backend already closed the session
   * (401 EXPIRED / REVOKED) — calling `stop` with a dead token would only
   * bounce with the same 401. A backend failure on the explicit exit is
   * ignored on purpose: the row expires by itself and the user must never be
   * stuck inside a session because `stop` failed.
   *
   * Everyone lands on `/prospects`. Since 31/08/2026 the picker lives inside
   * `/select-tenant`, and that route bounces to `/prospects` whenever a scoped
   * token exists — which is exactly what `clear()` preserves — so sending the
   * actor there would only produce a redirect. See `landingPath`.
   */
  async exit(options: { notifyBackend: boolean; noticeKey?: string }): Promise<void> {
    if (options.notifyBackend && this.getToken()) {
      try {
        await firstValueFrom(this.stop());
      } catch {
        // see JSDoc — ignored on purpose
      }
    }
    this.clear();
    if (options.noticeKey) sessionStorage.setItem(PENDING_NOTICE_KEY, options.noticeKey);
    this.reloadAs(await this.landingPath());
  }

  /** Returns (and forgets) the i18n key left by `exit` before the reload, if any. */
  consumePendingNotice(): string | null {
    const key = sessionStorage.getItem(PENDING_NOTICE_KEY);
    if (key) sessionStorage.removeItem(PENDING_NOTICE_KEY);
    return key;
  }

  /**
   * Where the actor lands after leaving — `/prospects` for everybody.
   *
   * It used to be `/impersonate` for a SuperAdmin, so it could pick another
   * tenant right away; that screen is gone (the picker is now the business
   * selector) and `/select-tenant` bounces while the actor's scoped token
   * exists — which is exactly what `clear()` preserves. Starting a SECOND
   * session is not this method's job: the actor lands on `/prospects` and
   * gets back to the picker from the business badge in the topbar
   * (`LayoutComponent.switchBusiness`), which drops that scoped token first.
   */
  async landingPath(): Promise<string> {
    return '/prospects';
  }

  /** Full reload — the only safe way to switch identity (see class JSDoc). Used after `start` too. */
  reloadAs(path: string): void {
    this.document.defaultView?.location.assign(path);
  }

  private writeMeta(meta: ImpersonationMeta): void {
    localStorage.setItem(IMPERSONATION_STORAGE_KEY.META, JSON.stringify(meta));
    this._meta.set(meta);
  }

  private readMeta(): ImpersonationMeta | null {
    if (!localStorage.getItem(IMPERSONATION_STORAGE_KEY.SESSION)) return null;
    const raw = localStorage.getItem(IMPERSONATION_STORAGE_KEY.META);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as ImpersonationMeta;
    } catch {
      return null;
    }
  }
}
